import { Image } from '@chakra-ui/react';
import { Button, Chip } from '@mui/material';
import { FaDownload } from 'react-icons/fa6';
import { ProtectedRoute } from '@partiu/api/components/ProtectedRoute.tsx';
import {
  DashboardHooks,
  useAgreementBanners,
  useAgreementCompanies,
  useAgreementMagazines,
  useAgreementOnlineCompanies,
  useAgreementPromocodeBatches,
  useAgreementPromocodeBatchesAggByTag,
  useAgreementPushNotifications,
  useAgreementStaffs,
  useDeleteAgreementBannerMutation,
  useDeleteAgreementPushNotificationMutation,
  useDeleteAgreementStaffMutation,
  useReportsList,
  useUsage,
} from '@partiu/api/dashboard/hooks.ts';
import { ReferralHook, useReferrals, useUpdateReferralMutation } from '@partiu/api/referrals/hooks.ts';
import { IBanner } from '@partiu/api/types';
import { AgreementCompanyStatus } from '@partiu/ui/components/molecules/AgreementCompanyStatus.tsx';
import { slugDashResolver } from '@partiu/ui/resolvers/slugResolvers.ts';
import { AutoLoginPage } from '@partiu/ui/templates/AutoLoginTemplate.tsx';
import { RootTemplate } from '@partiu/ui/templates/RootTemplate.tsx';
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ReactDOM from 'react-dom/client';
import { Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { DownloadPromocodeButton } from './components/atoms/DownloadPromocodeButton.tsx';
import { AgreementCompanyForm } from './components/forms/AgreementCompanyForm.tsx';
import { BannerForm } from './components/forms/BannerForm.tsx';
import { MagazineForm } from './components/forms/MagazineForm.tsx';
import { PromocodeForm } from './components/forms/PromocodeForm.tsx';
import { PushNotificationForm } from './components/forms/PushNotificationForm.tsx';
import { StaffForm } from './components/forms/StaffForm.tsx';
import { ToggleAgreementOnlineCompanySwitch } from './components/molecules/ToggleAgreementOnlineCompanySwitch.tsx';
import { ToggleBannerSwitch } from './components/molecules/ToggleBannerSwitch.tsx';
import { DataTablePage } from './components/templates/DataTablePage.tsx';
import { DashboardLayout } from './components/templates/DashboardTemplate.tsx';
import { MEDIA_MANAGER_HIDDEN_AGREEMENTS, RequireModule } from './components/templates/RequireModule.tsx';
import {
  DataGridCurrencyFormatter,
  DataGridDateFormatter,
  DataGridStaffLevelFormatter,
  DataGridTextFormatter,
} from './formatters.ts';
import './index.css';
import { CustomEmailPage } from './pages/CustomEmail.tsx';
import { DashboardPage } from './pages/Dashboard.tsx';
import { LoginPage } from './pages/Login.tsx';
import { NotificationCentralWrapper } from './pages/NotificationCentral/index.tsx';
import CentralNotification from './pages/CentralNotification.tsx';
import { MagazinePage } from './pages/Magazine.tsx';
import CustomerCentralPage from './pages/CustomerCentralPage/index.tsx';
import BooksReportPage from './pages/BooksReport.tsx';
import IntegrationTestPage from './pages/IntegrationTestPage.tsx';
import MapeamentoPlanosPage from './pages/SuperApp/MapeamentoPlanosPage.tsx';
import ModulosPage from './pages/SuperApp/ModulosPage.tsx';
import ClientesSincronizadosPage from './pages/SuperApp/ClientesSincronizadosPage.tsx';
import { LoginsLogsPage } from './pages/LoginsLogs.tsx';
import { SubscriptionsPage } from './pages/Subscriptions.tsx';
import { SupportChatPage } from './pages/SupportChat.tsx';
import { FinancialPage } from './pages/Financial/FinancialPage.tsx';

const downloadFile = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', ''); // This will trigger the download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild;
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels, ArcElement);

ChartJS.defaults.set('plugins.datalabels', {
  backgroundColor: '#dedede',
  borderRadius: 5,
  font: {
    size: 10,
  },
});

if (process.env.REACT_APP_SENTRY_ENVIRONMENT === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? '0.0') || 0.0,
    release: process.env.REACT_APP_SENTRY_RELEASE || 'unknown-release',
  });
}

console.log(process.env.REACT_APP_SENTRY_ENVIRONMENT);
ReactDOM.createRoot(document.getElementById('root')!).render(
  <RootTemplate themeResolver={slugDashResolver}>
    <Route path={'/login/:token'} element={<AutoLoginPage />} />
    <Route path={'/'} element={<LoginPage />} />
    {/* Layout route: the header/sidebar shell mounts once and survives navigation */}
    <Route
      element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }
    >
    <Route
      path={'/dashboard'}
      element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={'/emails'}
      element={
        <ProtectedRoute>
          <CustomEmailPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={'/central-de-notificacoes/criar'}
      element={
        <ProtectedRoute>
          <RequireModule module="pushNotificationModule">
          <NotificationCentralWrapper />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/subscricoes'}
      element={
        <ProtectedRoute>
          <RequireModule module="cpfModule" agreementFlag="externalCodeSubscriptionModule">
          <SubscriptionsPage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/chat-de-suporte'}
      element={
        <ProtectedRoute>
          <SupportChatPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={'/logins'}
      element={
        <ProtectedRoute>
          <LoginsLogsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={'/utilizacoes'}
      element={
        <ProtectedRoute>
          <RequireModule module="hasClub">
          <DataTablePage
            columns={[
              {
                field: 'userFullName',
                headerName: 'Conveniado',
                flex: 1,
                minWidth: 200,
                valueFormatter: DataGridTextFormatter,
              },
              { field: 'discountName', headerName: 'Desconto', flex: 1, minWidth: 200 },
              { field: 'discountCompanyName', headerName: 'Empresa', flex: 1, minWidth: 200 },
              {
                field: 'saved',
                headerName: 'Economizado',
                width: 150,
                valueFormatter: DataGridCurrencyFormatter,
              },
              { field: 'createdAt', headerName: 'Data', width: 150, valueFormatter: DataGridDateFormatter },
            ]}
            searchPlaceholder={'Nome da Empresa'}
            modelName={'Utilizações'}
            listHook={useUsage}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/relatorios'}
      element={
        <ProtectedRoute>
          <DataTablePage
            columns={[
              { field: 'name', headerName: 'Nome', flex: 1, minWidth: 180 },
              {
                field: 'message',
                headerName: 'Mensagem',
                flex: 1,
                minWidth: 200,
                renderCell: ({ value }) => (
                  <span
                    style={{
                      display: 'block',
                      width: '100%',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.4,
                      padding: '4px 0',
                    }}
                  >
                    {value || '—'}
                  </span>
                ),
              },
              {
                field: 'status',
                headerName: 'Status',
                width: 120,
                renderCell: ({ value }) => {
                  const label =
                    value === 'done'
                      ? 'Finalizado'
                      : value === 'failed' || value === 'error'
                        ? 'Erro'
                        : value === 'created'
                          ? 'Criado'
                          : value === 'pending'
                            ? 'Pendente'
                            : 'Gerando...';
                  const color =
                    value === 'done'
                      ? 'success'
                      : value === 'failed' || value === 'error'
                        ? 'error'
                        : 'warning';
                  return (
                    <Chip
                      label={label}
                      color={color}
                      size="small"
                      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                    />
                  );
                },
              },
              {
                field: 'file',
                headerName: 'Baixar',
                width: 100,
                renderCell: ({ row }) => {
                  if (!row.file) return <span style={{ color: '#9e9e9e' }}>—</span>;
                  return (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<FaDownload size={12} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadFile(row.file);
                      }}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', py: 0.5 }}
                    >
                      Baixar
                    </Button>
                  );
                },
              },
              { field: 'createdAt', headerName: 'Data', width: 150, valueFormatter: DataGridDateFormatter },
            ]}
            canExport={false}
            modelName={'Relatórios'}
            listHook={useReportsList}
            dataGridProps={{ getRowHeight: () => 'auto' }}
          />
        </ProtectedRoute>
      }
    />
    <Route
      path={'/relatorios/livros'}
      element={
        <ProtectedRoute>
          <RequireModule module="hasMedia" hiddenForAgreements={MEDIA_MANAGER_HIDDEN_AGREEMENTS}>
          <BooksReportPage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/promocodes'}
      element={
        <ProtectedRoute>
          <RequireModule module="promocodeModule">
          <DataTablePage
            columns={[
              { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
              { field: 'quantity', headerName: 'Códigos', flex: 1, minWidth: 200 },
              { field: 'tag', headerName: 'Tag', flex: 1, minWidth: 200 },
              {
                field: 'validUntil',
                headerName: 'Válido Até',
                flex: 1,
                minWidth: 200,
                valueFormatter: DataGridDateFormatter,
              },
              {
                field: 'download',
                minWidth: 200,
                headerName: 'Ações',
                renderCell: ({ row }) => <DownloadPromocodeButton batchName={row.name} />,
              },
            ]}
            searchPlaceholder={'Nome do lote'}
            modelName={'Lote de Códigos'}
            listHook={useAgreementPromocodeBatches}
            addForm={PromocodeForm}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/promocodes/usage'}
      element={
        <ProtectedRoute>
          <RequireModule module="promocodeModule">
          <DataTablePage
            columns={[
              { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
              { field: 'tag', headerName: 'Tag', flex: 1, minWidth: 200 },
              { field: 'all', headerName: 'Total', flex: 1, minWidth: 200 },
              { field: 'activated', headerName: 'Utilizados', flex: 1, minWidth: 200 },
              { field: 'inactivated', headerName: 'Disponíveis', flex: 1, minWidth: 200 },
            ]}
            searchPlaceholder={'Tag'}
            modelName={'Utilizações por Tags'}
            listHook={useAgreementPromocodeBatchesAggByTag}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/banners'}
      element={
        <ProtectedRoute>
          <DataTablePage
            columns={[
              { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
              {
                field: 'type',
                headerName: 'Tipo',
                width: 140,
                valueFormatter: ({ value }) => (value === 'book' ? 'Livro' : 'Desconto'),
              },
              {
                field: 'image',
                headerName: 'Imagem',
                flex: 1,
                renderCell: ({ value }) => <Image height={'100%'} objectFit="contain" src={value} />,
              },
              {
                field: 'imageMobile',
                headerName: 'Imagem Mobile',
                flex: 1,
                renderCell: ({ value }) => <Image height={'100%'} objectFit="contain" src={value} />,
              },
              {
                field: 'excludeAgreements',
                headerName: 'Ativo',
                width: 100,
                renderCell: ({ value, row }) => <ToggleBannerSwitch value={value} banner={row} />,
              },
            ]}
            modelName={'Banner'}
            listHook={useAgreementBanners}
            addForm={BannerForm}
            deleteMutationHook={useDeleteAgreementBannerMutation}
            selectable={true}
            isRowSelectable={({ row }) => (row as IBanner).exclusiveAgreements.length > 0}
            canExport={false}
          />
        </ProtectedRoute>
      }
    />
    <Route
      path={'/lojas'}
      element={
        <ProtectedRoute>
          <RequireModule module="localModule">
          <DataTablePage
            columns={[
              { field: 'name', headerName: 'Nome Fantasia', flex: 1, minWidth: 200 },
              {
                field: 'owner',
                headerName: 'Responsável',
                flex: 1,
              },
              {
                field: 'category',
                headerName: 'Categoria',
                flex: 1,
              },
              {
                field: 'createdAt',
                headerName: 'Data de Entrada',
                flex: 1,
                valueFormatter: DataGridDateFormatter,
              },
              {
                field: 'status',
                headerName: 'Status',
                flex: 2,
                renderCell: ({ row }) => <AgreementCompanyStatus company={row} />,
              },
            ]}
            modelName={'Lojas Parceiras'}
            searchPlaceholder={'empresa'}
            listHook={useAgreementCompanies}
            addForm={AgreementCompanyForm}
            addFormLabel={'Cadastrar loja'}
            modalSize={{ base: '95%', lg: '900px' }}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/lojas-online'}
      element={
        <ProtectedRoute>
          <RequireModule module="ecommerceModule">
          <DataTablePage
            columns={[
              {
                field: 'logo',
                headerName: 'Logo',
                flex: 1,
                renderCell: ({ value }) => <Image height={'100%'} objectFit="contain" src={value} />,
              },
              { field: 'showName', headerName: 'Nome', flex: 1, minWidth: 200 },
              {
                field: 'excludeAgreements',
                headerName: 'Ocultar E-commerce',
                width: 200,
                renderCell: ({ value, row }) => (
                  <ToggleAgreementOnlineCompanySwitch value={value} companyID={row.discountId} />
                ),
              },
            ]}
            modelName={'Lojas Parceiras Online'}
            searchPlaceholder={'empresa'}
            listHook={useAgreementOnlineCompanies}
            canExport={false}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/indicacoes'}
      element={
        <ProtectedRoute>
          <RequireModule agreementFlag="referralModule">
          <DataTablePage
            modelName={'Indicações'}
            columns={[
              { field: 'name', headerName: 'Nome', flex: 1 },
              { field: 'email', headerName: 'Email', flex: 1 },
              { field: 'telephone', headerName: 'Telefone', flex: 1 },
              { field: 'document', headerName: 'CPF', flex: 1 },
              {
                field: 'status',
                headerName: 'Status',
                width: 160,
                renderCell: (params) => {
                  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
                    confirmed: { label: 'Confirmado', color: 'success' },
                    created: { label: 'Criado', color: 'warning' },
                    expired: { label: 'Expirado', color: 'error' },
                    paid: { label: 'Pago', color: 'info' },
                  };
                  const s = map[params.value] ?? { label: params.value, color: 'info' };
                  return <Chip label={s.label} color={s.color} size="small" />;
                },
              },
              { field: 'createdFrom', headerName: 'Confirmado Por', flex: 1 },
              { field: 'createdBy', headerName: 'Indicado Por', flex: 1 },
              { field: 'createdAt', headerName: 'Criado em', flex: 1, valueFormatter: DataGridDateFormatter },
            ]}
            searchPlaceholder={'Nome, Email ou Indicador'}
            listQueryKey={ReferralHook.Referrals}
            listHook={useReferrals}
            updateMutationHook={useUpdateReferralMutation}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/revistas'}
      element={
        <ProtectedRoute>
          <RequireModule module="digitalMagazinesModule">
          <DataTablePage
            modelName={'Revista'}
            columns={[
              { field: 'issueNumber', headerName: 'Edição', flex: 1 },
              { field: 'file', headerName: 'Arquivo', flex: 1 },
              { field: 'createdAt', headerName: 'Criado Em', flex: 1, valueFormatter: DataGridDateFormatter },
            ]}
            searchPlaceholder={'Nome do Arquivo'}
            listQueryKey={DashboardHooks.Magazine}
            addForm={MagazineForm}
            listHook={useAgreementMagazines}
            canExport={false}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/push'}
      element={
        <ProtectedRoute>
          <RequireModule module="pushNotificationModule">
          <DataTablePage
            modelName={'Push Notification'}
            columns={[
              { field: 'title', headerName: 'Título', flex: 1 },
              { field: 'date', headerName: 'Data de Lançamento', flex: 1, valueFormatter: DataGridDateFormatter },
            ]}
            listQueryKey={DashboardHooks.PushNotifications}
            listHook={useAgreementPushNotifications}
            deleteMutationHook={useDeleteAgreementPushNotificationMutation}
            addForm={PushNotificationForm}
            selectable={true}
            canExport={false}
          />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/funcionarios'}
      element={
        <ProtectedRoute>
          <DataTablePage
            modelName={'Funcionario'}
            columns={[
              { field: 'fullName', headerName: 'Nome', flex: 1 },
              { field: 'email', headerName: 'Email', flex: 1 },
              {
                field: 'agreementSuperuser',
                headerName: 'Nível de Acesso',
                flex: 1,
                valueFormatter: DataGridStaffLevelFormatter,
              },
              { field: 'createdAt', headerName: 'Criado Em', flex: 1, valueFormatter: DataGridDateFormatter },
            ]}
            listQueryKey={DashboardHooks.Staff}
            searchPlaceholder={'Nome, Email ou CPF'}
            listHook={useAgreementStaffs}
            deleteMutationHook={useDeleteAgreementStaffMutation}
            addForm={StaffForm}
            selectable={true}
            isEditable={true}
          />
        </ProtectedRoute>
      }
    />
    <Route
      path={'/revistas-digitais'}
      element={
        <ProtectedRoute>
          <RequireModule module="digitalMagazinesModule">
          <MagazinePage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    {/* <Route
      path={'/revistas-digitais'}
      element={
        <ProtectedRoute>
          <DataTablePage
            modelName={'Magazine'}
            columns={[
              { field: 'articleTitle', headerName: 'Título do Artigo', flex: 1 },
              {
                field: 'fileUrl',
                headerName: 'Arquivo',
                flex: 1,
                renderCell: (params) => <button onClick={() => downloadFile(params.value)}>Download</button>,
              },
              { field: 'createdAt', headerName: 'Criado Em', flex: 1, valueFormatter: DataGridDateFormatter },
            ]}
            listQueryKey={DashboardHooks.Magazine}
            searchPlaceholder={'Título do Artigo ou Acordo'}
            listHook={useAgreementMagazines}
            selectable={true}
            isEditable={true}
          />
        </ProtectedRoute>
      }
    /> */}
    <Route
      path={'/central-de-notificacoes'}
      element={
        <ProtectedRoute>
          <RequireModule module="pushNotificationModule">
          <CentralNotification />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/super-app/mapeamento-planos'}
      element={
        <ProtectedRoute>
          <RequireModule module="hasSuperApp">
          <MapeamentoPlanosPage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/super-app/modulos'}
      element={
        <ProtectedRoute>
          <RequireModule module="hasSuperApp">
          <ModulosPage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/super-app/clientes-sincronizados'}
      element={
        <ProtectedRoute>
          <RequireModule module="hasSuperApp">
          <ClientesSincronizadosPage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/central-de-clientes'}
      element={
        <ProtectedRoute>
          <RequireModule module="customerCentralModule">
          <CustomerCentralPage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/financeiro'}
      element={
        <ProtectedRoute>
          <RequireModule agreementFlag="financialDashboard">
            <FinancialPage />
          </RequireModule>
        </ProtectedRoute>
      }
    />
    <Route
      path={'/teste-integracao'}
      element={
        <ProtectedRoute>
          <IntegrationTestPage />
        </ProtectedRoute>
      }
    />
    {/* <Route
      path={'/importacao'}
      element={
        <ProtectedRoute>
          <DataTablePage
            canExport={false}
            customButton={ToggleEmail}
            modelName={'Modulo de Importação'}
            columns={[
              {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                valueFormatter: ({ value }) => {
                  if (value === 'done') return 'Finalizado';
                  if (value === 'pending') return 'Pendente';
                },
              },
              {
                field: 'users_amount',
                headerName: 'Usuarios Importados',
                flex: 1,
                minWidth: 200,

                valueFormatter: ({ value }) => {
                  return value ? value : 0;
                },
              },
              { field: 'createdAt', headerName: 'Criado em', flex: 1, valueFormatter: DataGridDateFormatter },
            ]}
            listQueryKey={DashboardHooks.Importations}
            listHook={useGetImportations}
          />
        </ProtectedRoute>
      }
    /> */}

    {/*     <Route
      path={'/central-de-notificacoes'}
      element={
        <ProtectedRoute>
          <DataTablePage
            modelName={'Central de Notificações'}
            columns={[
              { field: 'notificationType', headerName: 'Tipo de notificação', flex: 1 },
              { field: 'emailSubject', headerName: 'Assunto do Email', flex: 1 },
              { field: 'pushSubject', headerName: 'Assunto do Push', flex: 1 },
              {
                field: 'scheduleType',
                headerName: 'Recorrente',
                flex: 1,
                valueFormatter: ({ value }) => (value === 'recurring' ? 'Sim' : 'Não'),
              },
              { field: 'createdAt', headerName: 'Criado em', flex: 1, valueFormatter: DataGridDateFormatter },
            ]}
            listQueryKey={NotificationHooks.Notifications}
            searchPlaceholder={'Assunto do email ou Assunto do push'}
            listHook={useGetAgreementNotifications}
            addFormPath={'/central-de-notificacoes/criar'}
            selectable={true}
            isEditable={true}
            deleteMutationHook={useDeleteAgreementNotification}
          />
        </ProtectedRoute>
      }
    /> */}
    </Route>
  </RootTemplate>
);
